import React, { useState, useEffect } from "react";
import HowToVoteRoundedIcon from "@mui/icons-material/HowToVoteRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import PollOutlinedIcon from "@mui/icons-material/PollOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { CircularProgress } from "@mui/material";
import { getAllVotesWithFilters, getVoteDetailsById, submitVote } from "../../../api/Vote";
import { getToken, getUserId } from "../../../api/index";
import "./EmbeddedAttachments.css";

export default function EmbeddedCommunityBallot({ voteId, initialData }) {
  const [ballot, setBallot] = useState(initialData || null);
  const [loading, setLoading] = useState(!initialData);
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [voting, setVoting] = useState(false);
  const [hasVoted, setHasVoted] = useState(Boolean(initialData?.isVoted));
  const [votedOptionId, setVotedOptionId] = useState(initialData?.selectedOptionId || initialData?.votedOptionId || null);
  const [errorMsg, setErrorMsg] = useState("");

  const currentUserId = getUserId();
  const token = getToken();

  useEffect(() => {
    if (voteId) {
      fetchBallotDetails();
    }
  }, [voteId]);

  const fetchBallotDetails = async () => {
    try {
      if (!initialData) setLoading(true);
      setErrorMsg("");

      // 1. Try getVoteDetailsById directly
      try {
        const details = await getVoteDetailsById(
          {
            voteId: voteId,
            userId: currentUserId || undefined,
          },
          token
        );

        const ballotData = details?.data || details;
        if (ballotData && (ballotData.voteId || ballotData.id || ballotData.title)) {
          setBallot(ballotData);
          if (ballotData.isVoted) {
            setHasVoted(true);
            setVotedOptionId(ballotData.selectedOptionId || ballotData.votedOptionId || ballotData.userVotedOptionId);
          }
          return;
        }
      } catch (err) {
        console.warn("getVoteDetailsById failed, attempting list fallback:", err);
      }

      // 2. Fallback to getAllVotesWithFilters strictly matching voteId (no random fallback)
      const res = await getAllVotesWithFilters(
        {
          userId: currentUserId || undefined,
          skip: 0,
          take: 50,
        },
        token
      );

      const items = Array.isArray(res) ? res : res?.data || [];
      const found = items.find((b) => String(b.voteId || b.id).toLowerCase() === String(voteId).toLowerCase());

      if (found) {
        setBallot(found);
        if (found.isVoted) {
          setHasVoted(true);
          setVotedOptionId(found.selectedOptionId || found.votedOptionId || found.userVotedOptionId);
        }
      } else if (!initialData) {
        setBallot(null);
      }
    } catch (err) {
      console.error("Error fetching attached ballot:", err);
      if (!initialData) setBallot(null);
    } finally {
      setLoading(false);
    }
  };

  const options = ballot?.options || ballot?.votingOption || [];
  const totalVotes = options.reduce((sum, opt) => sum + (Number(opt.voteCount) || Number(opt.count) || 0), 0) || Number(ballot?.totalVotes) || Number(ballot?.totalUserResponse) || 0;

  const handleCastVote = async (optionId) => {
    if (hasVoted || voting || !currentUserId) return;

    try {
      setVoting(true);
      setErrorMsg("");
      setSelectedOptionId(optionId);

      const targetVoteId = ballot?.voteId || ballot?.id || voteId;
      const res = await submitVote({
        voteId: targetVoteId,
        userId: currentUserId,
        optionId: optionId,
      });

      // Check for error payload from API
      if (res?.error || res?.success === false || (res?.message && res.message.toLowerCase().includes("error"))) {
        const errorText = res.error || res.message || "Failed to submit vote on ballot.";
        console.error("Error submitting vote:", errorText);
        setErrorMsg(errorText);
        return;
      }

      // Successful vote
      setHasVoted(true);
      setVotedOptionId(optionId);

      // Optimistically increment option vote count
      setBallot((prev) => {
        if (!prev) return prev;
        const updatedOptions = (prev.votingOption || prev.options || []).map((opt) => {
          const optId = opt.id || opt.optionId;
          if (String(optId) === String(optionId)) {
            return { ...opt, voteCount: (Number(opt.voteCount) || 0) + 1 };
          }
          return opt;
        });
        return {
          ...prev,
          isVoted: true,
          votedOptionId: optionId,
          votingOption: updatedOptions,
          options: updatedOptions,
          totalVotes: (Number(prev.totalVotes) || 0) + 1,
        };
      });
    } catch (err) {
      console.error("Failed to submit vote on attached ballot:", err);
      const errMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to submit vote.";
      setErrorMsg(errMsg);
    } finally {
      setVoting(false);
    }
  };

  if (loading && !ballot) {
    return (
      <div className="embedded-ballot-card embedded-loading">
        <CircularProgress size={24} style={{ color: "#48D1CC" }} />
        <span>Loading attached ballot...</span>
      </div>
    );
  }

  if (!ballot) {
    return (
      <div className="embedded-ballot-card embedded-fallback">
        <PollOutlinedIcon style={{ fontSize: 20, color: "#64748b" }} />
        <span>Attached Ballot (ID: {String(voteId).substring(0, 8)}...)</span>
      </div>
    );
  }

  return (
    <div className="embedded-ballot-card">
      {/* Top Header */}
      <div className="embedded-ballot-header">
        <div className="embedded-ballot-badge">
          <HowToVoteRoundedIcon style={{ fontSize: 16, color: "#48D1CC" }} />
          <span>Attached Ballot</span>
        </div>
      </div>

      <h4 className="embedded-ballot-title">{ballot.title}</h4>
      {ballot.description && (
        <p className="embedded-ballot-description">{ballot.description}</p>
      )}

      {/* Error Alert */}
      {errorMsg && (
        <div className="embedded-ballot-error-alert">
          <span>⚠️ {errorMsg}</span>
        </div>
      )}

      {/* Options List */}
      <div className="embedded-ballot-options-list">
        {options.map((opt) => {
          const optId = opt.id || opt.optionId;
          const count = opt.voteCount || opt.count || 0;
          const percentage = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
          const isVotedForThis = String(votedOptionId) === String(optId);

          return (
            <div
              key={optId}
              className={`embedded-ballot-option-row ${hasVoted ? "voted-mode" : "interactive-mode"} ${
                isVotedForThis ? "user-selected-option" : ""
              }`}
              onClick={() => !hasVoted && !voting && handleCastVote(optId)}
            >
              {/* Progress Background bar (if voted or viewing results) */}
              {hasVoted && (
                <div
                  className="embedded-option-progress-bar"
                  style={{ width: `${percentage}%` }}
                />
              )}

              <div className="embedded-option-content-wrap">
                <div className="embedded-option-left">
                  <div className="embedded-option-indicator">
                    {hasVoted ? (
                      isVotedForThis ? (
                        <CheckCircleRoundedIcon style={{ color: "#48D1CC", fontSize: 18 }} />
                      ) : (
                        <RadioButtonUncheckedRoundedIcon style={{ color: "#475569", fontSize: 18 }} />
                      )
                    ) : voting && selectedOptionId === optId ? (
                      <CircularProgress size={16} style={{ color: "#48D1CC" }} />
                    ) : (
                      <RadioButtonUncheckedRoundedIcon style={{ color: "#48D1CC", fontSize: 18 }} />
                    )}
                  </div>
                  <span className="embedded-option-text">{opt.value || opt.option || opt.optionText || opt.text || opt.title}</span>
                </div>

                {hasVoted && (
                  <div className="embedded-option-stats">
                    <span className="embedded-option-percent">{percentage}%</span>
                    <span className="embedded-option-votes-count">({count})</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer Info */}
      <div className="embedded-ballot-footer">
        <div className="embedded-ballot-stats">
          <span>👥 <strong>{totalVotes}</strong> total {totalVotes === 1 ? "vote" : "votes"}</span>
          {hasVoted && <span className="embedded-voted-badge">✓ You Voted</span>}
        </div>
        {!hasVoted && !currentUserId && (
          <span className="embedded-login-hint">Sign in to cast your vote</span>
        )}
      </div>
    </div>
  );
}
